import { Injectable } from "@angular/core";
import { StringHelper, Utilities } from "@tylertech/shared-module";
import
{
    ClassMetadata,
    PropertyMetadata,
    IGridViewMetadata
} from "@tylertech/tyler-api-types";
import { MetadataService } from "../metadata.service";
import { AugmentedModelObject, IAugmentedWithParent, RepositoryFormData } from "../../interfaces";
import { CachedRepositoryService } from "@tylertech/core-module";
import { ProviderStorageService } from "./provider.storage.service";
import { ObjectHelpers } from "../../utilities";
import { LogInvocation, LogLevel } from "@tylertech/logging";

export interface ITableFilter
{
    Top: number;
    OrderBy: string;
    Skip: number;
    Search: any;
    Properties: PropertyMetadata[];
    GroupedColumns: string[];
}
@Injectable({ providedIn: "root" })
export class MetadataTableService
{
    constructor(
        private $repositoryService: CachedRepositoryService,
        private $metadataService: MetadataService,
        private $providerStorageService: ProviderStorageService
    ) { }

    SetTable(
        key: string,
        metadata: ClassMetadata,
        entities: any[],
        viewMetadata: IGridViewMetadata
    ): Promise<any>
    {
        return this.AugmentModel(metadata, entities, viewMetadata.SelectProperty ? viewMetadata.SelectProperty : "Selected")
            .then(() =>
            {
                this.$providerStorageService.LocalEntitiesStore[key] = entities;
                this.$providerStorageService.FilteredEntities[key] = [];
            });
    }

    AugmentModel(metadata: ClassMetadata, entities: any[], selectProperty: string): Promise<void>
    {
        const loaders: Promise<void>[] = [];
        for (let i = 0; i < entities.length; i++)
        {
            const entity: IAugmentedWithParent = entities[i];
            const index = i;
            if (!entity["$isEntityWrapper"])
            {
                loaders.push(
                    this.$metadataService.AugmentModel(metadata.TypeName, entity, selectProperty)
                        .then(r =>
                        {
                            entities[index] = r;
                        })
                );
            }
        }
        return Promise.all(loaders)
            .then(() =>
            {
                /* Squashing Array<void>... */
            });
    }

    GetId(idProperty: string, model: any, index: number): string
    {
        return ObjectHelpers.GetId(idProperty, model, index);
    }

    GetPagedEntities(key: string, filter: ITableFilter, entities?: any[]): any[]
    {
        if (Utilities.IsNullOrUndefined(entities))
            entities = this.$providerStorageService.LocalEntitiesStore[key].filter(x => x.SerializedRowState !== "Deleted");
        else
            entities = entities.filter(x => x.SerializedRowState !== "Deleted");
        if (!this.$providerStorageService.IsInfiniteScroll(key))
            this.$providerStorageService.TotalHits[key] = entities.length;
        entities = this.topSkip(entities, filter.Top, filter.Skip)();
        return entities;
    }

    FetchRowModel(row: any, datacontroller: RepositoryFormData, viewMetadata: IGridViewMetadata): Promise<any>
    {
        if (viewMetadata && "RowTypeName" in viewMetadata.GridProperties)
        {
            const rowTypeName = viewMetadata.GridProperties["RowTypeName"] as string;
            const idProperty = viewMetadata.GridProperties["IdProperty"];
            return datacontroller.findExistingOrGet(rowTypeName, idProperty, row.Id, this.$repositoryService)
                .then(entity =>
                {
                    datacontroller.Add(rowTypeName, entity);
                    entity.$$TypeName = rowTypeName;
                    if (!entity.$$Parent)
                        entity.$$Parent = row["$$model"].$$Parent;
                    return entity;
                });
        }
        return Promise.resolve(row["$$model"]);
    }

    CanDo(type: string, model: any, isViewOnly: boolean, viewMetadata: IGridViewMetadata): boolean
    {
        const canDo = viewMetadata.GridProperties[`Can${type}`] && !isViewOnly;
        const canDoProp = viewMetadata.GridProperties[`Can${type}Property`];
        if (!canDo) return false;
        if (!Utilities.IsNullOrUndefined(canDoProp))
            return this.$metadataService.EvaluateBoolean(canDoProp, model["$$Parent"], model);
        return canDo;
    }

    private topSkip<T>(entities: Array<T>, top: number, skip: number): () => Array<T>
    {
        const l = entities.length;
        if (!skip)
        {
            skip = 0;
        }

        if (!top)
        {
            top = l;
        }

        return () =>
        {
            const end = skip + top;
            const part = entities.slice(skip, end);
            skip = end < l ? end : 0;
            return part;
        };
    }
}
